import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import './App.css';
import SearchBar from './components/SearchBar';
import GlobeViewer from './components/GlobeViewer';
import CameraInfo from './components/CameraInfo';
import SearchFeedback from './components/SearchFeedback';
import About from './components/About';
import ContextMenu from './components/ContextMenu';
import clientCacheService from './services/cacheService';

const INITIAL_LOCATION = {
  latitude: 20,
  longitude: 5,
  displayName: 'openflex',
  radius: 25000,
  isGlobal: true
};

function mergeUniqueCameras(existing, incoming) {
  if (!Array.isArray(incoming) || incoming.length === 0) {
    return existing;
  }

  const merged = [...existing];
  const seen = new Set(existing.map(camera => camera.id));

  incoming.forEach(camera => {
    if (!camera?.id) {
      return;
    }

    if (seen.has(camera.id)) {
      const index = merged.findIndex(item => item.id === camera.id);
      if (index >= 0) {
        const previous = merged[index];
        const normalized = { ...camera };

        Object.keys(normalized).forEach(key => {
          if (normalized[key] === '' || normalized[key] === null || normalized[key] === undefined) {
            if (previous[key] !== undefined && previous[key] !== null && previous[key] !== '') {
              normalized[key] = previous[key];
            }
          }
        });

        merged[index] = { ...previous, ...normalized };
      }
      return;
    }

    seen.add(camera.id);
    merged.push(camera);
  });

  return merged;
}

function App() {
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [activeResultIndex, setActiveResultIndex] = useState(-1);
  const [isSearching, setIsSearching] = useState(false);
  const [focusedMarkerId, setFocusedMarkerId] = useState(null);
  const [location, setLocation] = useState(INITIAL_LOCATION);
  const [hasSearched, setHasSearched] = useState(false);
  const [isEngaged, setIsEngaged] = useState(false);
  const [error, setError] = useState('');
  const [isBooting, setIsBooting] = useState(true);
  const [currentStage, setCurrentStage] = useState(null);
  const [searchTitle, setSearchTitle] = useState('');
  const [isAboutPage, setIsAboutPage] = useState(false);
  const eventSourceRef = useRef(null);

  const cameraMarkers = useMemo(() => searchResults
    .slice(0, 4200)
    .filter(camera => Number.isFinite(camera.latitude) && Number.isFinite(camera.longitude))
    .map(camera => ({
      id: camera.id,
      latitude: camera.latitude,
      longitude: camera.longitude,
      title: camera.title,
      image: camera.image,
      country: camera.country,
      availabilityStatus: camera.availabilityStatus
    })), [searchResults]);

  const activeCamera = activeResultIndex >= 0 ? searchResults[activeResultIndex] || null : null;
  const activeMarkerId = activeCamera?.id || null;

  useEffect(() => {
    let active = true;

    clientCacheService.clearOldCaches();

    async function loadInitialCameras() {
      try {
        const response = await axios.get('/api/cameras', {
          params: {
            mode: 'featured',
            limit: 140
          }
        });

        if (!active || !response.data?.success) {
          return;
        }

        setLocation(INITIAL_LOCATION);
      } catch (requestError) {
        if (!active) {
          return;
        }
        setError('Failed to load public streams.');
      } finally {
        if (active) {
          setIsBooting(false);
        }
      }
    }

    loadInitialCameras();

    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    const handler = event => {
      const root = document.querySelector('.app');
      if (!root) return;
      const ripple = document.createElement('span');
      ripple.className = 'openflex-ripple';
      const size = Math.max(root.clientWidth, root.clientHeight) * 0.06;
      ripple.style.width = `${size}px`;
      ripple.style.height = `${size}px`;
      ripple.style.left = `${event.clientX - size / 2}px`;
      ripple.style.top = `${event.clientY - size / 2}px`;
      root.appendChild(ripple);
      window.setTimeout(() => ripple.remove(), 650);
    };
    window.addEventListener('pointerdown', handler, { passive: true });
    return () => window.removeEventListener('pointerdown', handler);
  }, []);


  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);


  const handleEngage = useCallback(() => {
    setIsEngaged(true);
  }, []);

  const handleAboutToggle = useCallback(() => {
    setIsAboutPage(!isAboutPage);
  }, [isAboutPage]);

  const handleCancelSearch = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsSearching(false);
  }, []);

  const handleSearch = async query => {
    const normalizedQuery = String(query || '').trim();

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    const cached = clientCacheService.getCachedSearch(normalizedQuery);

    setIsSearching(true);
    setCurrentStage('started');
    setSearchTitle(normalizedQuery || 'Global Stream Index');
    setError('');
    setHasSearched(true);
    setIsEngaged(true);
    setSelectedCamera(null);
    setFocusedMarkerId(null);
    setSearchResults(cached?.cameras || []);

    if (cached?.location) {
      setLocation(cached.location);
    }

    if (cached?.cameras?.length > 0) {
      setActiveResultIndex(0);
      setFocusedMarkerId(cached.cameras[0]?.id || null);
    } else {
      setActiveResultIndex(-1);
    }

    try {
      const params = new URLSearchParams({ query: normalizedQuery });
      const eventSource = new EventSource(`/api/search/stream?${params}`);
      eventSourceRef.current = eventSource;
      let latestCameras = Array.isArray(cached?.cameras) ? [...cached.cameras] : [];
      let newLocation = null;

      eventSource.addEventListener('message', (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.stage === 'end') {
            eventSource.close();
            eventSourceRef.current = null;
            const finalLocation = newLocation || cached?.location || INITIAL_LOCATION;
            
            if (newLocation) {
              setLocation(newLocation);
            } else {
              setLocation(finalLocation);
            }
            
            setSearchResults(latestCameras);
            setIsSearching(false);
            setCurrentStage(latestCameras.length > 0 ? 'complete-success' : 'complete-empty');

            clientCacheService.cacheSearch(normalizedQuery, {
              location: finalLocation,
              cameras: latestCameras,
              count: latestCameras.length
            });
            
            if (latestCameras.length > 0) {
              setActiveResultIndex(0);
              setFocusedMarkerId(latestCameras[0]?.id || null);
            }
            return;
          }

          if (data.error) {
            eventSource.close();
            eventSourceRef.current = null;
            setIsSearching(false);
            setCurrentStage('error');
            setError(data.error);
            return;
          }

          if (data.stage) {
            setCurrentStage(data.stage);
          }

          if (data.title && !searchTitle) {
            setSearchTitle(data.title);
          }

          if (data.location) {
            newLocation = data.location;
          }

          if (data.cameras && Array.isArray(data.cameras)) {
            latestCameras = mergeUniqueCameras(latestCameras, data.cameras);
            setSearchResults(latestCameras);
            
            if (latestCameras.length > 0 && activeResultIndex < 0) {
              setActiveResultIndex(0);
              setFocusedMarkerId(latestCameras[0]?.id || null);
            }
          }
        } catch (err) {
        }
      });

      eventSource.addEventListener('error', () => {
        eventSource.close();
        eventSourceRef.current = null;
        setIsSearching(false);
        setCurrentStage(latestCameras.length === 0 ? 'complete-empty' : 'complete-success');
        if (latestCameras.length === 0) {
          setError('No streams found.');
          setActiveResultIndex(-1);
          setFocusedMarkerId(null);
        }
      });
    } catch (requestError) {
      setIsSearching(false);
      setCurrentStage('error');
      const message = requestError.response?.data?.error || 'Search failed.';
      setError(message);
      setActiveResultIndex(-1);
      setFocusedMarkerId(null);
    }
  };

  const handleMarkerClick = useCallback(markerId => {
    const nextIndex = searchResults.findIndex(camera => camera.id === markerId);
    if (nextIndex >= 0) {
      setActiveResultIndex(nextIndex);
      setFocusedMarkerId(markerId);
      setSelectedCamera(searchResults[nextIndex]);
    }
  }, [searchResults]);

  const handleResultStep = useCallback(direction => {
    if (searchResults.length <= 1) {
      return;
    }

    const nextIndex = activeResultIndex < 0
      ? 0
      : (activeResultIndex + direction + searchResults.length) % searchResults.length;

    setActiveResultIndex(nextIndex);
    setFocusedMarkerId(searchResults[nextIndex]?.id || null);
    setSelectedCamera(searchResults[nextIndex]);
  }, [activeResultIndex, searchResults]);

  const handleResultCardClick = useCallback(() => {
    if (activeCamera) {
      setSelectedCamera(activeCamera);
    }
  }, [activeCamera]);

  return (
    <div className={`app ${hasSearched ? 'app-active' : isEngaged ? 'app-engaged' : 'app-idle'}`}>
      <div className="app-shell">
        <div className={`search-progress ${isSearching ? "search-progress-active" : ""}`} aria-hidden="true" />
        <div className={`globe-stage ${hasSearched ? 'globe-stage-active' : 'globe-stage-idle'}`}>
          <GlobeViewer
            location={location}
            markers={cameraMarkers}
            activeMarkerId={activeMarkerId}
            focusedMarkerId={focusedMarkerId}
            onMarkerClick={handleMarkerClick}
            isBooting={isBooting}
            hasSearched={hasSearched}
            isSearching={isSearching}
          />
        </div>

        <SearchFeedback 
          isSearching={isSearching}
          stage={currentStage}
          resultCount={searchResults.length}
          hasResults={isSearching ? null : (searchResults.length > 0 ? true : false)}
          hasSearched={hasSearched}
          searchTitle={searchTitle}
        />

        <div className={`search-layer ${selectedCamera ? 'search-layer-with-preview' : ''} ${hasSearched ? 'search-layer-top' : isEngaged ? 'search-layer-engaged' : 'search-layer-centered'}`}>
          {selectedCamera && hasSearched && activeCamera && (
            <div className="result-navigator result-navigator-side" aria-label="Search results navigation">
              {searchResults.length > 1 && (
                <button
                  type="button"
                  className="result-arrow"
                  onClick={() => handleResultStep(-1)}
                  aria-label="Previous result"
                >
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M15 5L8 12L15 19" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              )}

              <button type="button" className="result-card" onClick={handleResultCardClick}>
                <span className="result-card-index">
                  {Math.max(activeResultIndex + 1, 1)}
                  <span>/</span>
                  {searchResults.length || 1}
                </span>
                <strong>{activeCamera.title}</strong>
                <span>
                  {[activeCamera.city, activeCamera.region, activeCamera.country].filter(Boolean).join(', ') || 'Public result'}
                </span>
              </button>

              {searchResults.length > 1 && (
                <button
                  type="button"
                  className="result-arrow"
                  onClick={() => handleResultStep(1)}
                  aria-label="Next result"
                >
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              )}
            </div>
          )}

          <div className="search-stack">
            <button 
              className={`brand-badge brand-badge-btn ${hasSearched ? 'brand-badge-compact' : 'brand-badge-hero'}`}
              onClick={handleAboutToggle}
              aria-label="About openflex"
            >
              <span className="brand-open">open</span>
              <span className="brand-flex">flex</span>
            </button>

            <SearchBar
              onSearch={handleSearch}
              onCancel={handleCancelSearch}
              onEngage={handleEngage}
              isSearching={isSearching}
              hasSearched={hasSearched}
              resultCount={searchResults.length}
            />

            {hasSearched && activeCamera && !selectedCamera && (
              <div className="result-navigator" aria-label="Search results navigation">
                {searchResults.length > 1 && (
                  <button
                    type="button"
                    className="result-arrow"
                    onClick={() => handleResultStep(-1)}
                    aria-label="Previous result"
                  >
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M15 5L8 12L15 19" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                )}

                <button type="button" className="result-card" onClick={handleResultCardClick}>
                  <span className="result-card-index">
                    {Math.max(activeResultIndex + 1, 1)}
                    <span>/</span>
                    {searchResults.length || 1}
                  </span>
                  <strong>{activeCamera.title}</strong>
                  <span>
                    {[activeCamera.city, activeCamera.region, activeCamera.country].filter(Boolean).join(', ') || 'Public result'}
                  </span>
                </button>

                {searchResults.length > 1 && (
                  <button
                    type="button"
                    className="result-arrow"
                    onClick={() => handleResultStep(1)}
                    aria-label="Next result"
                  >
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                )}
              </div>
            )}

            {error && <div className="status-banner status-banner-error">{error}</div>}
          </div>
        </div>
      </div>

      {selectedCamera && <CameraInfo camera={selectedCamera} onClose={() => setSelectedCamera(null)} onNavigate={handleResultStep} totalResults={searchResults.length} />}
      <ContextMenu isAboutPage={isAboutPage} onAboutClick={handleAboutToggle} />
      {isAboutPage && <About onIconClick={handleAboutToggle} />}
    </div>
  );
}

export default App;
